import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Star, Check, ArrowLeft, ImagePlus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;
const MAX_PHOTO_MB = 5;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const ReviewPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const orderId = params.get("order_id") || "";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]); // [{photo_id, url, preview, uploading}]
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: `/review?order_id=${orderId}` } });
      return;
    }
    if (!orderId) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setOrder(res.data))
      .catch(() => toast.error("Order not found"))
      .finally(() => setLoading(false));
  }, [user, token, orderId, navigate]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file after remove
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos per review`);
      return;
    }
    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: JPG / PNG / WEBP only`);
        continue;
      }
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        toast.error(`${file.name}: larger than ${MAX_PHOTO_MB} MB`);
        continue;
      }
      const preview = URL.createObjectURL(file);
      // Insert placeholder, then upload
      const placeholder = { tempId: `${Date.now()}-${file.name}`, preview, uploading: true };
      setPhotos((prev) => [...prev, placeholder]);
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await axios.post(`${API}/reviews/upload-photo`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPhotos((prev) =>
          prev.map((p) =>
            p.tempId === placeholder.tempId
              ? { photo_id: data.photo_id, url: data.url, preview, uploading: false }
              : p
          )
        );
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
        setPhotos((prev) => prev.filter((p) => p.tempId !== placeholder.tempId));
        URL.revokeObjectURL(preview);
      }
    }
    setUploading(false);
  };

  const removePhoto = (photoOrTempId) => {
    setPhotos((prev) =>
      prev.filter((p) => {
        const match = p.photo_id === photoOrTempId || p.tempId === photoOrTempId;
        if (match && p.preview) URL.revokeObjectURL(p.preview);
        return !match;
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Pick a star rating");
      return;
    }
    if (!title.trim() || !content.trim()) {
      toast.error("Add a short title and your thoughts");
      return;
    }
    if (photos.some((p) => p.uploading)) {
      toast.error("Wait for photos to finish uploading");
      return;
    }
    setSubmitting(true);
    try {
      const productType = order?.items?.[0]?.config?.type
        || order?.items?.[0]?.config?.shape
        || "wood";
      const photoIds = photos.filter((p) => p.photo_id).map((p) => p.photo_id);
      await axios.post(
        `${API}/reviews`,
        { product_type: productType, rating, title, content, order_id: orderId, photos: photoIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] text-[#F4F1DE]/60 text-center">
        Loading...
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4 bg-[#0A0A0A]">
        <div className="max-w-md mx-auto bg-[#121212] border border-white/10 p-8" data-testid="review-success">
          <div className="w-12 h-12 rounded-lg bg-green-500/15 border border-green-500/30 flex items-center justify-center mb-5">
            <Check className="w-6 h-6 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-2">
            Thanks for the review!
          </h1>
          <p className="text-sm text-[#F4F1DE]/60 mb-6">
            Your feedback helps fellow reptile keepers find the right build — and helps us get better every batch.
          </p>
          <Link to="/">
            <Button className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold">
              Back to ReptiCraft
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-[#0A0A0A]">
      <div className="max-w-md mx-auto bg-[#121212] border border-white/10 p-8 text-[#F4F1DE]" data-testid="review-page">
        <h1 className="text-2xl font-bold font-['Space_Grotesk'] leading-tight mb-2">
          Leave a review
        </h1>
        {order && (
          <p className="text-xs text-[#F4F1DE]/50 mb-6 font-mono">
            Order #{order.id?.slice(0, 8).toUpperCase()}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Stars */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#F4F1DE]/60 mb-3">
              Your rating
            </label>
            <div className="flex gap-1" onMouseLeave={() => setHoverRating(0)} data-testid="star-row">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hoverRating || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHoverRating(n)}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${n}`}
                  >
                    <Star
                      className={`w-8 h-8 ${filled ? "fill-[#E07A5F] text-[#E07A5F]" : "text-[#F4F1DE]/20"}`}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#F4F1DE]/60 mb-2">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Perfect for my bearded dragon"
              className="bg-[#0A0A0A] border-white/10 text-[#F4F1DE]"
              maxLength={80}
              data-testid="review-title"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#F4F1DE]/60 mb-2">
              Your thoughts
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Build quality, fit, finish — anything that would help another reptile owner."
              className="w-full min-h-[120px] bg-[#0A0A0A] border border-white/10 rounded px-3 py-2 text-sm text-[#F4F1DE] placeholder-[#F4F1DE]/30 focus:border-[#E07A5F] focus:outline-none resize-y"
              maxLength={1000}
              data-testid="review-content"
            />
            <p className="text-[10px] text-[#F4F1DE]/30 mt-1 text-right">
              {content.length}/1000
            </p>
          </div>

          {/* Photos */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#F4F1DE]/60 mb-2">
              Add photos <span className="normal-case tracking-normal text-[#F4F1DE]/30">(optional, up to {MAX_PHOTOS})</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {photos.map((p) => (
                <div
                  key={p.photo_id || p.tempId}
                  className="relative w-20 h-20 border border-white/10 overflow-hidden bg-[#0A0A0A]"
                  data-testid={`review-photo-${p.photo_id || p.tempId}`}
                >
                  <img src={p.preview} alt="" className="w-full h-full object-cover" />
                  {p.uploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="spinner" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removePhoto(p.photo_id || p.tempId)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/80 text-white flex items-center justify-center hover:bg-red-500/90"
                    data-testid={`remove-photo-${p.photo_id || p.tempId}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label
                  className="w-20 h-20 border border-dashed border-white/20 flex flex-col items-center justify-center text-[#F4F1DE]/50 hover:border-[#E07A5F] hover:text-[#E07A5F] cursor-pointer transition-colors"
                  data-testid="add-photo-btn"
                >
                  <ImagePlus className="w-5 h-5 mb-1" />
                  <span className="text-[9px] uppercase tracking-widest">Add</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="photo-file-input"
                  />
                </label>
              )}
            </div>
            <p className="text-[10px] text-[#F4F1DE]/30 mt-2">
              JPG / PNG / WEBP, max {MAX_PHOTO_MB} MB each. Photos appear publicly with your review.
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting || uploading}
            className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold"
            data-testid="review-submit-btn"
          >
            {submitting ? "Submitting..." : uploading ? "Uploading photos..." : "Submit review"}
          </Button>
        </form>

        <Link
          to="/"
          className="mt-5 inline-flex items-center gap-1 text-xs text-[#F4F1DE]/40 hover:text-[#F4F1DE]/70 transition-colors"
        >
          <ArrowLeft className="w-3 h-3" />
          Skip for now
        </Link>
      </div>
    </div>
  );
};

export default ReviewPage;
